// アンケートのフリーページ（9 店。メールのリンク {EnqueteUrl}?q={guid} の先）。wuSiteResource2 の Shared/Enquete.ts
// （AngularJS 1.x の EnqueteApp / MainCtrl）の引き取り先（Issue #101）。API も OppSites の /api/Enquete/* から
// wuStorefront の /api/enquete へ移した（src/server/enquete/）。
//
// ページ本文は現行どおり（<div ng-app="EnqueteApp" ng-controller="MainCtrl as c"> の中に step 1〜3 の 3 ブロック）。
// AngularJS は読み込まず、テンプレートを自前で埋める:
//   - [ng-show="c.step == N"] … 1 入力 / 2 確認 / 3 送信完了。?q= が無ければどれも出さない（旧の step 0）
//   - [ng-repeat="item in c.enqueteInfo.enqueteQuestions"] … 設問ごとに複製。{{item.question}} / {{item.defaultAnser}}
//     [ng-show="item.questionMode == '…'"] で種類の欄だけ出す（複数行 textarea・一行 input・選択式 radio・商品リスト）
//   - [ng-repeat="anser in item.ansers"] … 選択肢ごとに radio を複製（name は設問ごとに分ける。旧は全部 ans0）
//   - [ng-repeat="item in c.enqueteInfo.buyProducts"] … 購入商品。リンクは店の中の /product/{管理番号}（相対）
//   - ng-click: c.step = 2（確認へ）/ c.step = 1（修正）/ c.send()（送信）
// ★名前・メールアドレスの欄（c.enqueteInfo.userName / mailAddress）は隠す。API も返さない・受けない（#100 の決めたこと 2）。
// ★9 店のずれ: {{c.enqueteInfo.firstText}}（comic-samurai）はプロファイルの前文で埋める。
//   {{c.enqueteInfo.fserName}}（fukuro.in・dansai。userName の綴り違いで旧も常に空）は空にする。ほかの {{ }} も空にする。
// ★旧との違い: 送信の失敗を見せる（旧は応答を見ずに「完了」を出した）・二度押しを止める・受注が無いときに案内を出す。
// ★旧 enquete.js がまだ読まれているページ（angular.module('EnqueteApp') が引ける）では何もしない。
// ★ng-app 属性は外さない（店の <style> が [ng-app="EnqueteApp"] で装飾している）。angular.min.js だけ残っていても、
//   モジュールが無いので angular の自動起動は注入の段で失敗し（コンソールにエラーが出るだけ）、DOM には触らない。
// ★プレビュー: ?wu_enquete_preview=1 は作り物の設問で描き、送信しても fetch しない（本番の回答を作らない）。

import { resolveDevApiOverride } from '../shared/dev-api-override'
import type { EnqueteInfo, EnqueteQuestion } from '../shared/enquete'

export const ENQUETE_API = 'https://front.workup-system.com/api/enquete'
export const APP_SELECTOR = '[ng-app="EnqueteApp"]'

export const TEXT = {
  notFound: 'アンケートの情報を読み込めませんでした。お送りしたメールのリンクから開き直してください。',
  loadFailed: 'アンケートを読み込めませんでした。時間をおいて再度お試しください。',
  sendFailed: '送信できませんでした。時間をおいて再度お試しください。',
  sendNotFound: 'このアンケートは受け付けられませんでした。お送りしたメールのリンクから開き直してください。',
}

/** 旧 enquete.js（AngularJS のモジュール EnqueteApp）が同じページに居るか */
export function hasOldEnqueteApp(win: { angular?: { module?: (name: string) => unknown } }): boolean {
  const ng = win.angular
  if (!ng || typeof ng.module !== 'function') return false
  try {
    ng.module('EnqueteApp')
    return true
  } catch {
    return false // 未登録なら angular が投げる
  }
}

/** メールのリンクの ?q=（旧 getUrlVars と同じく q）。空なら null */
export function readGuid(search: string): string | null {
  const q = new URLSearchParams(search).get('q')
  return q && q.trim() !== '' ? q.trim() : null
}

export type SendResult = 'ok' | 'notFound' | 'failed'

/** POST。200 → ok / 404 → notFound / それ以外・通信エラー → failed（投げない） */
export async function postAnswers(
  api: string,
  guid: string,
  ansers: (string | null)[],
  fetchFn: typeof fetch = fetch,
): Promise<SendResult> {
  try {
    const r = await fetchFn(api, {
      method: 'POST',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ guid, ansers }),
    })
    if (r.status === 404) return 'notFound'
    return r.ok ? 'ok' : 'failed'
  } catch {
    return 'failed'
  }
}

/** 設問の初期の回答（旧は defaultAnser をそのまま入力欄の初期値にしていた） */
export function initialAnswers(info: EnqueteInfo): string[] {
  return info.questions.map((q) => q.defaultAnswer)
}

export interface ViewState {
  step: 1 | 2 | 3
  sending: boolean
  error: string | null
}

/**
 * 画面の状態（DOM に依らない）。step の移り変わり・送信中の二度押し止め・失敗の表示を持つ。
 * render は状態が変わるたびに呼ばれる。
 */
export function createController(
  send: () => Promise<SendResult>,
  render: (s: ViewState) => void,
): { state: () => ViewState; toConfirm: () => void; back: () => void; submit: () => Promise<void> } {
  let s: ViewState = { step: 1, sending: false, error: null }
  const set = (next: Partial<ViewState>): void => {
    s = { ...s, ...next }
    render(s)
  }
  return {
    state: () => s,
    toConfirm: () => {
      if (s.step === 1) set({ step: 2, error: null })
    },
    back: () => {
      if (s.step === 2 && !s.sending) set({ step: 1, error: null })
    },
    submit: async () => {
      if (s.step !== 2 || s.sending) return // 二度押し・送信後の再送を止める
      set({ sending: true, error: null })
      const r = await send()
      if (r === 'ok') set({ step: 3, sending: false, error: null })
      else set({ sending: false, error: r === 'notFound' ? TEXT.sendNotFound : TEXT.sendFailed })
    },
  }
}

// ---- DOM -------------------------------------------------------------------

const STEP_EXPR = /^\s*c\.step\s*==\s*(\d)\s*$/
const MODE_EXPR = /^\s*item\.questionMode\s*==\s*'([^']*)'\s*$/
const BINDING = /\{\{\s*([^}]*?)\s*\}\}/g

/** テキストの {{ }} を埋める。resolve が undefined を返したものは空にする */
export function fillText(text: string, resolve: (expr: string) => string | undefined): string {
  return text.replace(BINDING, (_m, expr: string) => resolve(expr) ?? '')
}

function fillTextNodes(root: Node, resolve: (expr: string) => string | undefined): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const nodes: Text[] = []
  for (let n = walker.nextNode(); n; n = walker.nextNode()) nodes.push(n as Text)
  for (const t of nodes) {
    if (t.data.includes('{{')) t.data = fillText(t.data, resolve)
  }
}

/** ページ全体（設問の複製の外）の {{c.enqueteInfo.X}}。firstText だけ埋め、ほかは空 */
export function resolvePageBinding(expr: string, info: EnqueteInfo | null): string | undefined {
  if (expr === 'c.enqueteInfo.firstText') return info?.firstText ?? ''
  return undefined
}

function stepOf(el: Element): number | null {
  const m = STEP_EXPR.exec(el.getAttribute('ng-show') ?? '')
  return m ? Number(m[1]) : null
}

/** 名前・メールアドレスの欄を隠す（ステップのブロックの直下の子ごと） */
function hideIdentityRows(step: Element): void {
  const isIdentity = (s: string | null): boolean => !!s && /c\.enqueteInfo\.(userName|mailAddress)/.test(s)
  const targets = new Set<Element>()
  const climb = (el: Element | null): void => {
    let cur = el
    while (cur && cur.parentElement !== step) cur = cur.parentElement
    if (cur) targets.add(cur)
  }
  for (const el of Array.from(step.querySelectorAll('[ng-model]'))) {
    if (isIdentity(el.getAttribute('ng-model'))) climb(el)
  }
  const walker = document.createTreeWalker(step, NodeFilter.SHOW_TEXT)
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (isIdentity((n as Text).data)) climb(n.parentElement)
  }
  for (const t of targets) (t as HTMLElement).style.display = 'none'
}

/** [ng-repeat] の雛形を外して返す（同じ場所に描くための目印を残す） */
function takeTemplate(scope: Element, selector: string): { tpl: Element; mark: Comment } | null {
  const tpl = scope.querySelector(selector)
  if (!tpl || !tpl.parentNode) return null
  const mark = document.createComment('wu-enquete')
  tpl.parentNode.replaceChild(mark, tpl)
  tpl.removeAttribute('ng-repeat')
  return { tpl, mark }
}

function insertBefore(mark: Comment, nodes: Node[]): void {
  for (const n of nodes) mark.parentNode?.insertBefore(n, mark)
}

/** 入力（step 1）の設問 1 つ */
function buildInputQuestion(
  tpl: Element,
  q: EnqueteQuestion,
  index: number,
  info: EnqueteInfo,
  answers: string[],
): Element {
  const el = tpl.cloneNode(true) as Element

  // 種類ごとの欄: 合わない種類は消す（ng-show の代わり）
  for (const part of Array.from(el.querySelectorAll('[ng-show]'))) {
    const m = MODE_EXPR.exec(part.getAttribute('ng-show') ?? '')
    if (m && m[1] !== q.mode) part.remove()
  }

  // 選択肢（radio の雛形を選択肢の数だけ）
  const radio = takeTemplate(el, '[ng-repeat^="anser in item.ansers"]')
  if (radio) {
    insertBefore(
      radio.mark,
      q.choices.map((choice) => {
        const r = radio.tpl.cloneNode(true) as Element
        const input = r.querySelector<HTMLInputElement>('input[type="radio"]')
        if (input) {
          input.removeAttribute('ng-model')
          input.name = `wu-enquete-${index}`
          input.value = choice
          input.checked = answers[index] === choice
          input.addEventListener('change', () => {
            if (input.checked) answers[index] = choice
          })
        }
        fillTextNodes(r, (expr) => (expr === 'anser' ? choice : undefined))
        return r
      }),
    )
  }

  // 購入商品（商品リストの設問の中）
  const product = takeTemplate(el, '[ng-repeat^="item in c.enqueteInfo.buyProducts"]')
  if (product) {
    insertBefore(
      product.mark,
      info.products.map((p) => {
        const li = product.tpl.cloneNode(true) as Element
        const a = li.querySelector('a')
        if (a) a.setAttribute('href', `/product/${p.code}`)
        fillTextNodes(li, (expr) => (expr === 'item.name' ? p.name : undefined))
        return li
      }),
    )
  }

  // 複数行・一行の入力欄
  for (const input of Array.from(el.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('textarea[ng-model], input[ng-model]'))) {
    if (input.getAttribute('ng-model') !== 'item.defaultAnser' || input.type === 'radio') continue
    input.removeAttribute('ng-model')
    input.value = answers[index] ?? ''
    input.addEventListener('input', () => {
      answers[index] = input.value
    })
  }

  fillTextNodes(el, (expr) => (expr === 'item.question' ? q.question : undefined))
  return el
}

/** 確認・完了（step 2 / 3）の設問 1 つ */
function buildShowQuestion(tpl: Element, q: EnqueteQuestion, answer: string): Element {
  const el = tpl.cloneNode(true) as Element
  fillTextNodes(el, (expr) => (expr === 'item.question' ? q.question : expr === 'item.defaultAnser' ? answer : undefined))
  return el
}

function clickTargets(step: Element, expr: RegExp): HTMLElement[] {
  return Array.from(step.querySelectorAll<HTMLElement>('[ng-click]')).filter((b) => expr.test(b.getAttribute('ng-click') ?? ''))
}

export interface MountOptions {
  guid: string | null
  /** 設問を取る。null は受注なし（404）。投げたら読み込み失敗 */
  load: () => Promise<EnqueteInfo | null>
  send: (ansers: string[]) => Promise<SendResult>
}

/** ページに束ねる。ステップのブロックが無ければ何もしない */
export function mountEnquete(root: Element, opts: MountOptions): void {
  const steps = new Map<number, HTMLElement>()
  for (const el of Array.from(root.querySelectorAll<HTMLElement>('[ng-show]'))) {
    const n = stepOf(el)
    if (n !== null && !steps.has(n)) steps.set(n, el)
  }
  if (steps.size === 0) return

  const showStep = (n: number | null): void => {
    for (const [k, el] of steps) el.style.display = k === n ? '' : 'none'
  }
  showStep(null) // 読み込み前に生のテンプレート（{{ }}）を見せない

  const notice = (text: string): void => {
    const p = document.createElement('p')
    p.className = 'wu-enquete-notice'
    p.textContent = text
    root.insertBefore(p, root.firstChild)
  }

  if (!opts.guid) {
    fillTextNodes(root, () => undefined)
    return // 旧の step 0（何も出さない）
  }

  opts
    .load()
    .then((info) => {
      if (!info) {
        fillTextNodes(root, () => undefined)
        notice(TEXT.notFound)
        return
      }
      render(info)
    })
    .catch((e: unknown) => {
      console.warn('[wu] enquete: 設問の取得に失敗しました', e instanceof Error ? e.message : '')
      fillTextNodes(root, () => undefined)
      notice(TEXT.loadFailed)
    })

  function render(info: EnqueteInfo): void {
    const answers = initialAnswers(info)
    const qsel = '[ng-repeat^="item in c.enqueteInfo.enqueteQuestions"]'

    // 入力（step 1）
    const s1 = steps.get(1)
    if (s1) {
      const t = takeTemplate(s1, qsel)
      if (t) insertBefore(t.mark, info.questions.map((q, i) => buildInputQuestion(t.tpl, q, i, info, answers)))
      hideIdentityRows(s1)
    }

    // 確認・完了（step 2 / 3）。表示のたびに今の回答で描き直す
    const shows: { mark: Comment; tpl: Element; nodes: Element[] }[] = []
    for (const n of [2, 3]) {
      const s = steps.get(n)
      if (!s) continue
      const t = takeTemplate(s, qsel)
      if (t) shows.push({ ...t, nodes: [] })
      hideIdentityRows(s)
    }
    const redrawShows = (): void => {
      for (const s of shows) {
        for (const n of s.nodes) n.remove()
        s.nodes = info.questions.map((q, i) => buildShowQuestion(s.tpl, q, answers[i] ?? ''))
        insertBefore(s.mark, s.nodes)
      }
    }

    // ページ全体の残りの {{ }}（firstText / fserName など）
    fillTextNodes(root, (expr) => resolvePageBinding(expr, info))

    // 送信ボタンの下に失敗の案内を出す場所
    const s2 = steps.get(2)
    const sendButtons = s2 ? clickTargets(s2, /c\.send\(\)/) : []
    const error = document.createElement('p')
    error.className = 'wu-enquete-error'
    error.setAttribute('role', 'alert')
    error.style.display = 'none'
    error.style.color = '#c00'
    error.style.marginTop = '0.5em'
    const anchor = sendButtons[0]?.parentElement
    if (anchor) anchor.appendChild(error)

    const ctl = createController(
      () => opts.send(answers.map((a) => a)),
      (s) => {
        if (s.step !== 1) redrawShows()
        showStep(s.step)
        for (const b of sendButtons) (b as HTMLButtonElement).disabled = s.sending
        error.textContent = s.error ?? ''
        error.style.display = s.error ? '' : 'none'
        if (root.getBoundingClientRect().top < 0) root.scrollIntoView({ block: 'start' })
      },
    )
    const on = (step: Element | undefined, expr: RegExp, fn: () => void): void => {
      if (!step) return
      for (const b of clickTargets(step, expr)) {
        b.addEventListener('click', (ev) => {
          ev.preventDefault()
          fn()
        })
      }
    }
    on(steps.get(1), /c\.step\s*=\s*2/, ctl.toConfirm)
    on(s2, /c\.step\s*=\s*1/, ctl.back)
    on(s2, /c\.send\(\)/, () => void ctl.submit())

    showStep(1)
  }
}

// ---- 入口 ------------------------------------------------------------------

const PREVIEW_INFO: EnqueteInfo = {
  // ★作り物。実在のお客さまの受注ではない
  firstText: '（プレビュー）',
  questions: [
    { question: 'ご購入いただいた商品', mode: '商品リスト', choices: [], defaultAnswer: 'サンプル商品 A\r\nサンプル商品 B' },
    { question: '① ご購入いただきました商品の用途をお教えください。', mode: '複数行', choices: [], defaultAnswer: '' },
    { question: '② 一行の設問（プレビュー）', mode: '一行', choices: [], defaultAnswer: '' },
    {
      question: '※ 上記のお声についてお願いがあります。',
      mode: '選択式',
      choices: ['匿名なら出してもよい', '匿名でもNG'],
      defaultAnswer: '',
    },
  ],
  products: [{ name: 'サンプル商品 A', code: 1 }],
}

/**
 * ローカル検証（複製ページ + devserver）のための叩き先の上書き。**localhost / 127.0.0.1 だけ**受ける。
 * ★任意の URL を受けると、店の本物のドメインの上に他所の設問を出して回答（と guid）を他所へ送らせるリンクが作れてしまう。
 */
export function resolveApiBase(search: string): string {
  return resolveDevApiOverride(search, 'wu_enquete_api', ENQUETE_API)
}

export default function enquete(): void {
  const win = window as unknown as { angular?: { module?: (name: string) => unknown } }
  if (hasOldEnqueteApp(win)) return
  const root = document.querySelector(APP_SELECTOR)
  if (!root) return

  const params = new URLSearchParams(location.search)
  if (params.has('wu_enquete_preview')) {
    mountEnquete(root, {
      guid: 'preview',
      load: async () => PREVIEW_INFO,
      // fetch しない。本番の回答を作らない
      send: () => new Promise((resolve) => setTimeout(() => resolve(params.get('wu_enquete_preview') === 'fail' ? 'failed' : 'ok'), 400)),
    })
    return
  }

  const guid = readGuid(location.search)
  const api = resolveApiBase(location.search)
  mountEnquete(root, {
    guid,
    load: async () => {
      const r = await fetch(`${api}?guid=${encodeURIComponent(guid ?? '')}`, { cache: 'no-store' })
      if (r.status === 404) return null
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return (await r.json()) as EnqueteInfo
    },
    send: (ansers) => postAnswers(api, guid ?? '', ansers),
  })
}
